import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  BadgeCheck,
  ClipboardList,
  CloudCog,
  Crosshair,
  FileText,
  GraduationCap,
  Layers,
  Map as MapIcon,
  Monitor,
  Moon,
  Radar,
  Send,
  ShieldCheck,
  Sun,
  Users,
  UserCog,
  MapPin,
} from 'lucide-react'

import { Panel, Tabs, Button, EmptyHint } from './components/ui/Panels'
import { StateBadge, StateDot } from './components/ui/State'
import { MapView } from './components/map/MapView'
import { MAP_LAYER_META } from './components/map/CoordinateGridFallback'
import type { GridLine, GridPoint, GridPolygon } from './components/map/CoordinateGridFallback'
import { splitTraffic, trailToFeature, predictToFeature, twinOverlayToFeatures } from './components/map/layers'

import { SppiForm, DEFAULT_SPP, parseCoords, parseArea } from './components/pilot/SppiForm'
import { RemoteIdPanel } from './components/pilot/RemoteIdPanel'
import { MavCmdPanel } from './components/pilot/MavCmdPanel'

import { TrackTable, TrackSummary } from './components/situational/TrackTable'
import { StatusCounters } from './components/situational/StatusCounters'
import { RangeBearing } from './components/situational/RangeBearing'
import { TrustPanel } from './components/situational/TrustPanel'
import { L3Panel, type TrafficSource } from './components/situational/L3Panel'
import { ReplayPlayer } from './components/situational/ReplayPlayer'
import type { ReplayMapData } from './components/situational/ReplayPlayer'
import { IncidentsJournal } from './components/auditor/IncidentsJournal'
import { WeatherPanel, WeatherStrip } from './components/situational/WeatherPanel'
import type { WeatherSnapshot, IncidentsSnapshot } from './types'
import { CloudSun } from 'lucide-react'

import { NebosvodPanel, INITIAL_NEBIOSVOD } from './components/integrations/NebosvodPanel'
import { GeofencePanel } from './components/integrations/GeofencePanel'
import { UsersRolesPanel, SEED_USERS } from './components/manage/UsersRolesPanel'
import { PilotProfilePanel, DEFAULT_PROFILE } from './components/manage/PilotProfilePanel'
import { AuditorScreen } from './components/auditor/AuditorScreen'

import {
  createGeofence,
  deleteGeofence,
  fetchDeclarations,
  normalizeDeclarationStatus,
  fetchGeofences,
  fetchMavlink,
  fetchTraffic,
  fetchTrajectories,
  fetchTwinOverlay,
  fetchTrust,
  fetchIncidents,
  fetchWeather,
  reviewDeclaration,
  DeclarationConflictError,
  sendMavCommand,
} from './lib/api'
import { DECLARATION_STATUS_LABEL, DECLARATION_STATUS_TONE } from './lib/statusModel'
import { useAuthSession, roleLabel } from './lib/ivoAuth'
import {
  PILOT_TABS_BY_ROLE,
  ROLE_HOME,
  SCREEN_LABEL,
  SIT_TABS_BY_ROLE,
  roleOf,
  useIsMobile,
} from './lib/roles'
import type { PilotTabId, Screen, SitTabId } from './lib/roles'
import { useAsyncResource } from './hooks/useAsyncResource'
import { toTracks } from './lib/trackModel'
import type { Feature, LineString } from 'geojson'
import { padBbox, distanceM } from './lib/geo'
import { fmtAge, fmtClock, fmtDegradedReason, fmtDistance } from './lib/format'
import { THEME_LABELS, useTheme } from './theme/ThemeContext'
import type { UtTheme } from './theme/ThemeContext'
import type {
  AsyncState,
  ConsoleUser,
  DeclarationStatus,
  FlightDeclaration,
  Geofence,
  MavCommand,
  MavlinkTelemetry,
  NebosvodStatus,
  PilotProfile,
  Role,
  TrafficObservation,
  TrajSnapshot,
  TrustSnapshot,
  TwinOverlay,
} from './types'

/** Конфликтная пара из /api/trajectories.conflicts[h] (карточка по клику на дугу) */
type TrajConflict = TrajSnapshot['conflicts'][string][number]

/* ============================================================================
 * КОНСОЛЬ ИВО — КОРНЕВОЙ КОМПОНЕНТ
 *
 * Левая колонка  — ПАНЕЛЬ ПИЛОТА (светлая тема):
 *                    СППИ, телеметрия Remote ID, MAVLink-команды, декларации,
 *                    профиль пилота.
 * Центр          — КАРТА индикатора воздушной обстановки с деградацией
 *                  на координатную сетку при недоступности стиля/тайлов.
 * Правая колонка — СИТУАЦИОННЫЙ ЦЕНТР (тёмная тема, Air Boss):
 *                    цели, тревоги доверия, «Дальность и азимут», геозоны,
 *                    интеграция «Небосвод UTM».
 * Нижняя часть   — Управление пользователями (роли и рабочий процесс).
 *
 * Данные — реальные, с прокси на консольный сервер :8105.
 * Формат обмена данными — ГОСТ Р 71934-2025, СК WGS-84.
 * ========================================================================= */

/** Пробный район по умолчанию — центральная Россия (Москва) */
const DEFAULT_BBOX = { lat1: 55.60, lon1: 37.40, lat2: 55.90, lon2: 37.80 }

/** Интервалы опроса источников, мс */
const POLL = {
  trust: 5000,
  /** Траектории+прогноз: тик zone_guard 3 с, опрос 4 с — дуга живая */
  traj: 4000,
  mavlink: 2000,
  traffic: 10000,
  geo: 15000,
  declarations: 12000,
  /** Метео (задача №10): METAR обновляется раз в 30-60 мин — чаще опрашивать
   *  нет смысла. Консоль кэширует ответ ещё 300 с, поэтому фронтовый опрос
   *  раз в 5 мин не создаёт давления ни на консоль, ни на источник. */
  weather: 300000,
}

/* Вкладки. Типы живут в lib/roles вместе с таблицей прав — здесь только
   реэкспорт для совместимости с уже написанными обращениями. */
type PilotTab = PilotTabId
type SitTab = SitTabId

export function App() {
  const { theme, setTheme } = useTheme()
  /* Сессия приходит из <AuthGate> — App() монтируется только после входа, так
   * что здесь useAuthSession() всегда найдёт контекст (иначе бросит ошибку). */
  const auth = useAuthSession()

  /* ---------------------------------------------------------------- ИСТОЧНИКИ */
  // Подсистема доверия: alerts, anti_replay, cross_domain
  const trust = useAsyncResource<TrustSnapshot>((signal) => fetchTrust(signal), POLL.trust)

  // Журнал инцидентов (§6.3): постоянные записи в PostGIS, переживают рестарт
  const incidents = useAsyncResource<IncidentsSnapshot>((signal) => fetchIncidents(signal), POLL.trust)

  // Телеметрия MAVLink — таймаут, если БВС не подключён
  const mavlink = useAsyncResource<MavlinkTelemetry>((signal) => fetchMavlink(signal), POLL.mavlink)

  // Декларации — требуют работающего FB+Keycloak, обрабатываем деградацию
  const declarations = useAsyncResource((signal) => fetchDeclarations(signal), POLL.declarations)

  // Геозоны
  const geofences = useAsyncResource((signal) => fetchGeofences(signal), POLL.geo)
  // П.2 (2026-10-06): фильтр зон по источнику. Пусто = показывать все.
  const [zoneSrcFilter, setZoneSrcFilter] = useState<'' | 'mintc' | 'sppi' | 'own'>('')

  // Метео аэродрома (задача №10). Именно хук, а не ручной useEffect: он
  // сохраняет последние хорошие данные при сбое — метео не должно исчезать
  // с экрана из-за одной сетевой ошибки — и отдаёт статус деградации с
  // причиной вместе с данными.
  const weather = useAsyncResource<WeatherSnapshot>(
    (signal) => fetchWeather(signal),
    POLL.weather,
  )

  // Воздушный трафик по видимой области.
  // ВНИМАНИЕ: ключ запроса — `bbox`, формат "lat1,lon1,lat2,lon2"
  const [bbox, setBbox] = useState(DEFAULT_BBOX)
  // L3-тренажёр (2026-10-07i): источник карты — прод-FB или twin-сессия.
  // При переключении меняется URL запроса: useAsyncResource перезагружает
  // данные по ключу загрузчика (loader identity), поэтому src в замыкании.
  const [trafficSrc, setTrafficSrc] = useState<TrafficSource>('prod')
  const traffic = useAsyncResource<TrafficObservation[]>(
    (signal) => fetchTraffic(padBbox(bbox), signal, trafficSrc),
    POLL.traffic,
  )

  // Траектории (FR24) + прогноз движения (10/30/60/300 с) — задача
  // «траектории как FR24 + предсказание ~10 с» (2026-10-07).
  const traj = useAsyncResource<TrajSnapshot>(
    (signal) => fetchTrajectories(signal),
    POLL.traj,
  )

  /* Оверлей двойника (D3): «двойник vs реальность». Эндпоинт /api/l3/overlay
   * опционален — если консоль ещё не проксирует его (гейт рестарта), клиент
   * получит ошибку, useAsyncResource отдаст data=null и оверлей погаснет,
   * не ломая карту. POLL тот же, что у траекторий (живой тик 3-4 с). */
  const overlay = useAsyncResource<TwinOverlay>(
    (signal) => fetchTwinOverlay(signal),
    POLL.traj,
  )

  /* ----------------------------------------------------------- СОСТОЯНИЕ UI */
  /* ---- РОЛЬ И ЭКРАНЫ (задача №13) --------------------------------------
     Роль приходит из Keycloak и определяет: состав вкладок, стартовый экран
     и какие панели вообще показывать. Без этого аудитору предлагалось
     управление дронами, на которое он получает 403. */
  const role = roleOf(auth.me?.roles)
  /* РЕПЛЕЙ v2 (08z): инструктор = l3_instructor или ЦУП-диспетчер
   * (суперпользователь консоли) — семантика как в L3Panel. */
  const isInstructor =
    !!auth.me?.roles &&
    (auth.me.roles.includes('l3_instructor') ||
      auth.me.roles.includes('dispatcher'))
  const isMobile = useIsMobile()
  const [mobileScreen, setMobileScreen] = useState<Screen>(() =>
    role ? ROLE_HOME[role] : 'map',
  )

  // Роль приходит после монтирования (useAuth), поэтому стартовый экран
  // выставляем эффектом, а не инициализатором — иначе всем был бы «Карта».
  useEffect(() => {
    if (role) setMobileScreen(ROLE_HOME[role])
  }, [role])

  /** Экраны, доступные этой роли на телефоне */
  const availableScreens = useMemo<Screen[]>(() => {
    if (role === 'auditor') return ['auditor', 'map']
    if (role === 'pilot') return ['pilot', 'map']
    return ['map', 'pilot', 'situational']
  }, [role])

  const [pilotTab, setPilotTab] = useState<PilotTab>('sppi')
  const [sitTab, setSitTab] = useState<SitTab>('tracks')

  /* Активная вкладка не должна оставаться в закрытом для роли состоянии:
     смена роли (перезаход) иначе оставила бы пустую панель. */
  const pilotTabs = useMemo(
    () => (role ? PILOT_TABS_BY_ROLE[role] : PILOT_TABS_BY_ROLE.auditor),
    [role],
  )
  const sitTabs = useMemo(
    () => (role ? SIT_TABS_BY_ROLE[role] : SIT_TABS_BY_ROLE.auditor),
    [role],
  )
  useEffect(() => {
    if (!pilotTabs.includes(pilotTab)) setPilotTab(pilotTabs[0] ?? 'profile')
  }, [pilotTabs, pilotTab])
  // L3-вкладка (2026-10-07i): видна и по l3-ролям Keycloak, которых нет в
  // SIT_TABS_BY_ROLE (тип IvoRole их не описывает) — эффект сброса вкладки
  // учитывает это иначе «Тренажёр L3» у стажёра мигнёт и сбросится.
  const hasL3Role = !!auth.me?.roles?.some((r) => r.startsWith('l3_'))
  useEffect(() => {
    if (sitTab === 'l3' && hasL3Role) return
    if (!sitTabs.includes(sitTab)) setSitTab(sitTabs[0] ?? 'tracks')
  }, [sitTabs, sitTab, hasL3Role])
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null)
  const [ownshipId, setOwnshipId] = useState<string | null>(null)
  /** Карточка конфликтной пары (2026-10-07g): callsign борта, по дуге кликнули.
   *  Сама пара (оба борта, miss_m, tca_s, горизонт) ищется в traj.data. */
  const [conflictCardDrone, setConflictCardDrone] = useState<string | null>(null)
  const [showLayers, setShowLayers] = useState(true)
  /** Оверлей двойника (D3) — показ слоя «двойник vs реальность» */
  const [showTwinOverlay, setShowTwinOverlay] = useState(false)
  /** Режим 3D-карты: объёмные геозоны/районы + наклон камеры (задача №14) */
  const [is3d, setIs3d] = useState(false)
  const [mavBusy, setMavBusy] = useState(false)
  const [lastCommand, setLastCommand] = useState<{ cmd: MavCommand; ok: boolean; text: string } | null>(null)
  const [geoBusy, setGeoBusy] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [lastSubmitted, setLastSubmitted] = useState<FlightDeclaration | null>(null)
  const [nebosvod, setNebosvod] = useState<NebosvodStatus>(INITIAL_NEBIOSVOD)
  const [nebosvodSyncing, setNebosvodSyncing] = useState(false)
  const [users, setUsers] = useState<ConsoleUser[]>(SEED_USERS)
  const [profile, setProfile] = useState<PilotProfile>(DEFAULT_PROFILE)

  /* -------------------------------------------------------------- МОДЕЛЬ ЦЕЛЕЙ */
  /* РЕПЛЕЙ v2 (08z): режим плеера. replayData != null = карта показывает
   * записанный кадр twin-сцены (наблюдения + траектории + события) вместо
   * живого потока. Модель данных та же (toTracks/trajFeats ниже берут
   * подмену) — MapView не знает, что показывает запись. */
  const [replayData, setReplayData] = useState<ReplayMapData | null>(null)
  const liveTraffic = traffic.data
  const liveTraj = traj.data
  const displayTraffic = useMemo(
    () => replayData?.observations ?? liveTraffic ?? [],
    [replayData, liveTraffic],
  )
  const displayTraj = replayData?.traj ?? liveTraj

  const tracks = useMemo(() => toTracks(displayTraffic), [displayTraffic])

  /* Хвосты + прогнозные дуги (FR24-стиль). Порядок uses: 
   *   trails — вся история окна ZG_TRAIL_S (60 с), тонкие зелёные;
   *   predict10 — дуга на 10 с (сплошная жёлтая): «куда придёт вот сейчас»;
   *   predict60 — дуга на 60 с (пунктир): плановый путь по маршруту;
   *   конфликтные дуги (2026-10-07f): участникам пары ближе ZG_PRED_SEP_M
   *   (500 м) на горизонте h свойство conflict=1 — слой predict красит их
   *   цветом нарушителей (data-выражение в layers.ts). Пары берутся ПО
   *   ГОРИЗОНТАМ: дуга 10с — из conflicts["10"], дуга 60с — из conflicts["60"].
   *   Пара может сближаться к 60с и уже расходиться к 10с — тогда красной
   *   будет только дуга 60с: дуга каждого горизонта честно отвечает
   *   «пара ближе порога ИМЕННО на этом горизонте». */
  const trajFeats = useMemo(() => {
    const snap = displayTraj
    if (!snap?.ok) return { trails: [], p10: [], p60: [] }
    const confHorizon = (h: string) =>
      new Set((snap.conflicts?.[h] ?? []).flatMap((c) => [c.a, c.b]))
    const conf10 = confHorizon("10")
    const conf60 = confHorizon("60")
    const trails: Array<Feature<LineString>> = []
    for (const t of snap.trails) {
      const f = trailToFeature(t.points, { drone: t.drone })
      if (f) trails.push(f)
    }
    const p10: Array<Feature<LineString>> = []
    const p60: Array<Feature<LineString>> = []
    for (const p of snap.predict) {
      const pr10 = p.predict["10"]
      const pr60 = p.predict["60"]
      if (pr10?.path?.length >= 2) {
        const f = predictToFeature(pr10.path, {
          drone: p.drone,
          conflict: conf10.has(p.drone) ? 1 : 0,
        })
        if (f) p10.push(f)
      }
      if (pr60?.method === 'route' && pr60.path?.length >= 2) {
        const f = predictToFeature(pr60.path, {
          drone: p.drone,
          conflict: conf60.has(p.drone) ? 1 : 0,
        })
        if (f) p60.push(f)
      }
    }
    return { trails, p10, p60 }
  }, [displayTraj])

  const twinOverlayFeats = useMemo(() => twinOverlayToFeatures(overlay.data), [overlay.data])
  const twinTrust = useMemo(() => overlay.data?.twin_overlay?.trust ?? null, [overlay.data])

  /* Карточка конфликтной пары (2026-10-07g). Клик по красной дуге даёт
   * callsign одного участника; пара ищется в conflicts["10"] и ["60"]
   * (борт может конфликтовать на одном горизонте и не на другом — берём
   * объединение, честно помечая горизонты). Мигание при tca<10с — по
   * минимальному tca_s среди горизонтов. Пару исчезла (пара разошлась /
   * поллинг обновился) — карточка закрывается сама. */
  const conflictPair = useMemo(() => {
    if (!conflictCardDrone || !traj.data?.ok) return null
    const snap = traj.data
    let miss: number | null = null
    let tca: number | null = null
    const horizons: string[] = []
    for (const h of ['10', '60']) {
      const c = (snap.conflicts?.[h] ?? []).find(
        (p) => p.a === conflictCardDrone || p.b === conflictCardDrone,
      )
      if (!c) continue
      horizons.push(h)
      if (miss === null || c.miss_m < miss) miss = c.miss_m
      if (tca === null || c.tca_s < tca) tca = c.tca_s
    }
    if (!horizons.length) return null
    const first = (snap.conflicts?.[horizons[0]] ?? []).find(
      (p) => p.a === conflictCardDrone || p.b === conflictCardDrone,
    )
    if (!first) return null
    return { pair: first, horizons, missM: miss as number, tcaS: tca as number }
  }, [conflictCardDrone, traj.data])
  useEffect(() => {
    if (conflictCardDrone && !conflictPair) setConflictCardDrone(null)
  }, [conflictCardDrone, conflictPair])
  const ownship = useMemo(
    () => tracks.find((t) => t.id === ownshipId) ?? null,
    [tracks, ownshipId],
  )
  const selected = useMemo(
    () => tracks.find((t) => t.id === selectedTrackId) ?? null,
    [tracks, selectedTrackId],
  )

  /* --------------------------------------------------------- ДАННЫЕ ДЛЯ КАРТЫ */
  // Цели для заглушки: координаты + вид цели
  const gridPoints = useMemo<GridPoint[]>(
    () =>
      tracks
        .filter((t) => t.lat !== null && t.lon !== null)
        .map((t) => ({
          id: t.id,
          lat: t.lat as number,
          lon: t.lon as number,
          label: t.callsign,
          kind: t.id === ownshipId ? 'ownship' : t.kind === 'intruder' ? 'intruder' : t.kind,
        })),
    [tracks, ownshipId],
  )

  // Маршруты деклаций
  const gridLines = useMemo<GridLine[]>(() => {
    const out: GridLine[] = []
    for (const [i, d] of (declarations.data ?? []).entries()) {
      const route = Array.isArray(d.route) ? (d.route as Array<[number, number]>) : null
      if (route && route.length >= 2) {
        out.push({
          id: `decl-${d.id ?? i}`,
          points: route,
          color: MAP_LAYER_META.find((l) => l.id === 'declarations')?.color ?? '#f59e0b',
        })
      }
    }
    return out
  }, [declarations.data])

  // Районы деклараций и геозоны
  const gridPolygons = useMemo<GridPolygon[]>(() => {
    const out: GridPolygon[] = []
    // Полоса высот из строки/числа -> число. Заявки хранят alt_low/alt_high,
    // геозоны — lower_limit/upper_limit; в 3D это и есть объём зоны.
    const num = (v: unknown): number | undefined => {
      const n = Number(v)
      return Number.isFinite(n) ? n : undefined
    }

    for (const [i, d] of (declarations.data ?? []).entries()) {
      const area = Array.isArray(d.area) ? (d.area as Array<[number, number]>) : null
      if (area && area.length >= 3) {
        // ⚠️ Именованный параметр kind решает, в какой источник попадёт
        // полигон на настоящей карте (см. MapView 3-втор): зоны -> SRC.geofences,
        // районы заявок -> SRC.missions. Раньше признака не было, и все
        // полигоны попадали в один источник.
        out.push({
          id: `decl-area-${d.id ?? i}`,
          kind: 'mission',
          points: area,
          color: MAP_LAYER_META.find((l) => l.id === 'declarations')?.color ?? '#f59e0b',
          label: `Заявка ${d.id ?? i + 1}`,
          lowerLimit: num(d.alt_low ?? d.altLow),
          upperLimit: num(d.alt_high ?? d.altHigh),
        })
      }
    }
    for (const [i, g] of (geofences.data ?? []).entries()) {
      // п.2: фильтр по источнику ('' = все)
      if (zoneSrcFilter && (g.source ?? 'own') !== zoneSrcFilter) continue
      const poly = Array.isArray(g.polygon) ? (g.polygon as Array<[number, number]>) : null
      if (poly && poly.length >= 3) {
        const lowerLimit = num(g.lower_limit)
        const upperLimit = num(g.upper_limit)
        out.push({
          id: `geo-${g.id ?? i}`,
          kind: 'geofence',
          points: poly,
          color: MAP_LAYER_META.find((l) => l.id === 'geofences')?.color ?? '#a78bfa',
          label: g.name ?? `Геозона ${i + 1}`,
          // ASL, м: консоль отдаёт строки «120.00»/«0.00»
          lowerLimit,
          upperLimit,
          altitudeRef: Number(g.altitude_ref ?? 0),
          groundMin: (g.ground_min ?? null),
          groundMax: (g.ground_max ?? null),
          source: (g.source ?? 'own') as 'mintc' | 'sppi' | 'own',
        })
      }
    }
    return out
  }, [declarations.data, geofences.data, zoneSrcFilter])

  /* --------------------------------------------------------- ДЕЙСТВИЯ ОПЕРАТОРА */

  /** Отправка MAVLink-команды с обработкой ошибок */
  const handleMavCommand = useCallback(async (cmd: MavCommand, alt: number) => {
    setMavBusy(true)
    try {
      const res = await sendMavCommand(cmd, 1, alt)
      setLastCommand({ cmd, ok: true, text: JSON.stringify(res) })
    } catch (err) {
      setLastCommand({
        cmd,
        ok: false,
        text: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setMavBusy(false)
    }
  }, [])

  /** Согласование / отклонение декларации.
   *  §6.2 (2026-10-07): 409 от сервера (район заявки пересекает запретную
   *  зону) -> состояние reviewConflict: показываем зоны и кнопки
   *  «Одобрить всё равно» (force) / «Отклонить». */
  const [reviewConflict, setReviewConflict] = useState<{ id: string; zones: string[]; message: string } | null>(null)
  const handleReview = useCallback(
    async (id: string, approve: boolean, force = false) => {
      try {
        await reviewDeclaration(id, approve, 'air-boss-console', force)
        setReviewConflict((prev) => (prev?.id === id ? null : prev))
        declarations.refresh()
      } catch (err) {
        if (err instanceof DeclarationConflictError) {
          setReviewConflict({ id, zones: err.zones, message: err.message })
          return
        }
        // Ошибка отображается в статусе источника
      }
    },
    [declarations],
  )

  /** Создание геозоны */
  const handleCreateGeofence = useCallback(
    async (fence: Geofence) => {
      setGeoBusy(true)
      try {
        await createGeofence(fence)
        geofences.refresh()
      } catch {
        /* статус источника отразит ошибку */
      } finally {
        setGeoBusy(false)
      }
    },
    [geofences],
  )

  /** Удаление геозоны */
  const handleDeleteGeofence = useCallback(
    async (id: string) => {
      setGeoBusy(true)
      try {
        await deleteGeofence(id)
        geofences.refresh()
      } catch {
        /* игнорируем */
      } finally {
        setGeoBusy(false)
      }
    },
    [geofences],
  )

  /** Отправка заявки в СППИ */
  const handleSubmitSppi = useCallback(
    async (payload: Record<string, unknown>) => {
      setSubmitting(true)
      try {
        // В прототипе заявка формируется и отражается в списке деклараций;
        // фактическая отправка зависит от работающего FB + Keycloak.
        setLastSubmitted({
          id: `СППИ-${String(Date.now()).slice(-6)}`,
          is_approved: false,
          uas_class: String(payload.uas_class ?? ''),
          alt_m: Number(payload.alt_m ?? 0),
          operator: String(payload.operator ?? ''),
          route: payload.route as Array<[number, number]> | undefined,
          area: payload.area as Array<[number, number]> | undefined,
        })
        declarations.refresh()
        void payload
      } finally {
        setSubmitting(false)
      }
    },
    [declarations],
  )

  /** Подключение/отключение «Небосвод UTM» */
  const handleNebosvodToggle = useCallback((connect: boolean) => {
    setNebosvod((prev) => ({
      ...prev,
      connected: connect,
      lastError: connect ? undefined : 'Отключено оператором',
    }))
  }, [])

  /** Синхронизация геозон с «Небосвод UTM» */
  const handleNebosvodSync = useCallback(() => {
    setNebosvodSyncing(true)
    window.setTimeout(() => {
      setNebosvod((prev) => ({
        ...prev,
        lastSync: new Date().toISOString(),
        geozonesSynced: geofences.data?.length ?? 0,
        lastError: undefined,
      }))
      setNebosvodSyncing(false)
    }, 800)
  }, [geofences.data])

  /* ------------------------------------------------- ОЦЕНКА РИСКА ПЕРЕСЕЧЕНИЯ */
  const conflict = useMemo(() => {
    if (!ownship) return null
    const clearance = Number(trust.data?.cross_domain?.min_alt_clearance_m ?? 60)
    for (const t of tracks) {
      if (t.id === ownship.id) continue
      if (t.lat === null || t.lon === null || ownship.lat === null || ownship.lon === null) continue
      const d = distanceM({ lat: ownship.lat, lon: ownship.lon }, { lat: t.lat, lon: t.lon })
      const altDiff =
        ownship.altM !== null && t.altM !== null ? Math.abs(t.altM - ownship.altM) : null
      if (d < 1000 && altDiff !== null && altDiff < clearance) {
        return { track: t, distance: d, altDiff }
      }
    }
    return null
  }, [ownship, tracks, trust.data])

  /* ------------------------------------------------------------- СВОДКА СВЯЗИ */
  const linkStates: Array<{ label: string; state: 'approved' | 'pending' | 'warning' | 'critical-error'; status: AsyncState<unknown>['status'] }> = [
    { label: 'Доверие', state: trust.status === 'ok' ? 'approved' : trust.status === 'degraded' ? 'warning' : trust.status === 'error' ? 'critical-error' : 'pending', status: trust.status },
    { label: 'Трафик', state: traffic.status === 'ok' ? 'approved' : traffic.status === 'degraded' ? 'warning' : traffic.status === 'error' ? 'critical-error' : 'pending', status: traffic.status },
    { label: 'Декларации', state: declarations.status === 'ok' ? 'approved' : declarations.status === 'degraded' ? 'warning' : declarations.status === 'error' ? 'critical-error' : 'pending', status: declarations.status },
    { label: 'БВС', state: mavlink.status === 'ok' ? 'approved' : mavlink.status === 'degraded' ? 'warning' : mavlink.status === 'error' ? 'critical-error' : 'pending', status: mavlink.status },
  ]

  /* ------------------------------------------------------------------ РЕНДЕР */
  return (
    <div className="flex h-full flex-col overflow-hidden bg-canvas text-ink">
      {/* ================= ШАПКА ================= */}
      {/* На телефоне шапка занимала 4 строки заголовка и выдавливала «Выйти» и
          переключатель тем за правый край (замер: кнопка 45×18, недоступна для
          пальца). Здесь: на мобильном — одна короткая строка, крупные цели. */}
      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line bg-surface px-2 py-1.5 sm:px-3 sm:py-2">
        <div className="flex items-center gap-2">
          <Radar size={20} className="text-accent" aria-hidden />
          <div>
            <div className="text-sm font-bold leading-tight tracking-wide">
              {/* На телефоне полное название занимало 4 строки — отдаём короткое */}
              <span className="hidden sm:inline">ЦУП · ИВО — Индикатор Воздушной Обстановки</span>
              <span className="sm:hidden">ЦУП · ИВО</span>
            </div>
            <div className="hidden text-[10px] leading-tight text-ink-muted sm:block">
              ГОСТ Р 71934-2025 · СК WGS-84 · высоты в метрах
            </div>
          </div>
        </div>

        <span className="flex-1" />

        {/* Индикаторы связи с источниками */}
        <div className="hidden items-center gap-3 lg:flex">
          {linkStates.map((l) => (
            <span key={l.label} className="flex items-center gap-1 text-[10px] text-ink-muted">
              <StateDot state={l.state} pulsing={l.status === 'loading'} />
              {l.label}
            </span>
          ))}
        </div>

        {/* Роль оператора — ИСТОЧНИК ПРАВ, а не декоративный селектор (задача №12).
            Раньше здесь стоял <select> с жёстко зашитыми «Air Boss / Пилот / …»,
            который ничего не ограничивал и вводил в заблуждение: роль приходит
            из Keycloak (/api/auth/me) и по ней реально режется доступ на бэкенде. */}
        <div className="flex items-center gap-1 rounded-md border border-line px-1.5 py-0.5">
          <UserCog size={12} className="text-ink-muted" aria-hidden />
          <span className="text-[10px] text-ink-muted">Роль:</span>
          <span className="max-w-[150px] truncate text-[11px] font-semibold" title={auth.me?.user || ''}>
            {roleLabel(auth.me?.roles)}
          </span>
        </div>

        {/* Кто вошёл + выход из сессии */}
        <div className="flex items-center gap-1.5 rounded-md border border-line px-1.5 py-0.5">
          <span className="max-w-[120px] truncate text-[10px] text-ink-muted" title={auth.me?.user || ''}>
            {auth.me?.user || '—'}
          </span>
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted hover:text-ink"
            onClick={() => {
              void auth.logout()
            }}
            disabled={auth.busy}
            title="Завершить сессию"
          >
            Выйти
          </button>
        </div>

        {/* Переключатель тем. На телефоне не помещался — подписи сокращены,
            а сам блок на мобильном уезжает на вторую строку шапки. */}
        <div className="flex shrink-0 overflow-hidden rounded-md border border-line">
          <button
            className={`ut-tab ${theme === 'pilot' ? 'ut-tab-active' : ''} min-h-[40px] px-2.5 sm:min-h-0 sm:px-1`}
            onClick={() => setTheme('pilot' satisfies UtTheme)}
            title={THEME_LABELS.pilot.hint}
            aria-label="Светлая тема — панель пилота"
          >
            <Sun size={12} className="inline" aria-hidden />
            <span className="hidden sm:inline"> Пилот</span>
          </button>
          <button
            className={`ut-tab ${theme === 'situational' ? 'ut-tab-active' : ''} min-h-[40px] px-2.5 sm:min-h-0 sm:px-1`}
            onClick={() => setTheme('situational' satisfies UtTheme)}
            title={THEME_LABELS.situational.hint}
            aria-label="Тёмная тема — ситуационный центр"
          >
            <Moon size={12} className="inline" aria-hidden />
            <span className="hidden sm:inline"> Ситуационный</span>
          </button>
        </div>

        <span className="ut-metric hidden text-[10px] text-ink-muted sm:inline">{fmtClock(new Date().toISOString())}</span>
      </header>

      {/* ============ МОБИЛЬНЫЙ ПЕРЕКЛЮЧАТЕЛЬ ЭКРАНОВ (задача №13) ============
          На телефоне три панели в столбик не помещаются: карта схлопывалась в
          контейнер высотой 0, страница не прокручивалась. Поэтому показываем
          ОДНУ панель и переключаем её. Роль задаёт состав вкладок и стартовый
          экран, но оператор может зайти и на соседние экраны. */}
      {isMobile && (
        <nav
          className="flex shrink-0 gap-1 border-b border-line bg-surface px-2 py-1.5"
          aria-label="Разделы консоли"
        >
          {availableScreens.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setMobileScreen(s)}
              aria-current={mobileScreen === s}
              className={`min-h-[44px] flex-1 rounded-md px-2 text-[12px] font-semibold ${
                mobileScreen === s
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-muted'
              }`}
            >
              {SCREEN_LABEL[s]}
            </button>
          ))}
        </nav>
      )}

      {/* ================= ОСНОВНАЯ ОБЛАСТЬ =================
          Десктоп: три колонки, как раньше. Мобильный: ОДНА панель во весь
          экран — иначе карта получала контейнер высотой 0. */}
      <main
        className={
          isMobile
            ? 'flex min-h-0 flex-1 flex-col gap-2 p-2'
            : 'grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] gap-2 p-2'
        }
      >
        {/* ---------- ПАНЕЛЬ ПИЛОТА (светлая) ----------
          На мобильном показывается только на своём экране — иначе все три
          панели делили бы высоту экрана и схлопывались в ноль. */}
        <div
          className={`${isMobile ? 'min-h-0 flex-1' : ''} flex min-h-0 flex-col gap-2`}
          style={isMobile ? { display: mobileScreen === 'pilot' ? 'flex' : 'none' } : undefined}
        >
          <Panel
            title="Панель пилота"
            icon={BadgeCheck}
            state={<StateBadge state={theme === 'pilot' ? 'active' : 'pending'}>СВЕТЛАЯ</StateBadge>}
            className="min-h-0 flex-1"
            bodyClassName="flex flex-col"
          >
            <div className="shrink-0 border-b border-line px-2 py-1.5">
              <Tabs<PilotTab>
                active={pilotTab}
                onChange={setPilotTab}
                tabs={[
                  ...(pilotTabs.includes('sppi')
                    ? [{ id: 'sppi' as const, label: 'СППИ', icon: MapPin }]
                    : []),
                  ...(pilotTabs.includes('remoteid')
                    ? [{ id: 'remoteid' as const, label: 'Remote ID', icon: Crosshair }]
                    : []),
                  ...(pilotTabs.includes('mav')
                    ? [{ id: 'mav' as const, label: 'БВС', icon: Send }]
                    : []),
                  ...(pilotTabs.includes('declarations')
                    ? [
                        {
                          id: 'declarations' as const,
                          label: 'Заявки',
                          icon: ClipboardList,
                          badge: declarations.data?.length,
                        },
                      ]
                    : []),
                  ...(pilotTabs.includes('declarations_read')
                    ? [
                        {
                          id: 'declarations_read' as const,
                          label: 'Журнал заявок',
                          icon: ClipboardList,
                        },
                      ]
                    : []),
                  ...(pilotTabs.includes('profile')
                    ? [{ id: 'profile' as const, label: 'Профиль', icon: FileText }]
                    : []),
                ]}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {pilotTab === 'sppi' && (
                <SppiForm
                  onSubmit={handleSubmitSppi}
                  submitting={submitting}
                  lastSubmitted={lastSubmitted}
                />
              )}
              {pilotTab === 'remoteid' && (
                <RemoteIdPanel
                  telemetry={mavlink.data}
                  connected={Boolean(mavlink.data?.connected)}
                  onRefresh={mavlink.refresh}
                />
              )}
              {pilotTab === 'mav' && (
                <MavCmdPanel onCommand={handleMavCommand} busy={mavBusy} lastCommand={lastCommand} />
              )}
              {pilotTab === 'declarations' && (
                <DeclarationsList
                  declarations={declarations}
                  onReview={handleReview}
                  weather={weather.data}
                  reviewConflict={reviewConflict}
                />
              )}
              {pilotTab === 'declarations_read' && (
                <DeclarationsList
                  declarations={declarations}
                  onReview={() => undefined}
                  weather={weather.data}
                  readOnly
                />
              )}
              {pilotTab === 'profile' && <PilotProfilePanel profile={profile} onChange={setProfile} />}
            </div>
          </Panel>
        </div>

        {/* ---------- КАРТА ----------
          Карта — единственная панель, которой нужна ВСЯ высота на телефоне,
          поэтому её экран по умолчанию для диспетчера. */}
        <div
          className={`${isMobile ? 'min-h-0 flex-1' : ''} flex min-h-0 flex-col gap-2`}
          style={isMobile ? { display: mobileScreen === 'map' ? 'flex' : 'none' } : undefined}
        >
          <Panel
            title="Воздушная обстановка"
            icon={MapIcon}
            className="min-h-0 flex-1"
            bodyClassName="relative"
            actions={
              <>
                {/* Режим 3D (задача №14): объёмные геозоны и районы.
                    Без наклона камеры extrusion не читается — поэтому кнопка
                    двигает не только слои, но и камеру (см. apply3dTo). */}
                <Button
                  size="sm"
                  variant={is3d ? 'primary' : 'default'}
                  onClick={() => setIs3d((v) => !v)}
                  title={is3d ? 'Вернуть 2D-вид' : 'Объёмный вид: геозоны и районы с полосой высот'}
                  aria-pressed={is3d}
                >
                  3D
                </Button>
                {/* П.2 (2026-10-06): фильтр зон по источнику (mintc/sppi/own) */}
                <Button
                  size="sm"
                  icon={MapIcon}
                  variant={zoneSrcFilter ? 'primary' : 'default'}
                  onClick={() =>
                    setZoneSrcFilter((v) =>
                      v === '' ? 'mintc' : v === 'mintc' ? 'sppi' : v === 'sppi' ? 'own' : '',
                    )
                  }
                  title="Фильтр зон по источнику: все → mintc (приказ №92) → sppi → свои"
                >
                  {zoneSrcFilter === ''
                    ? 'Зоны: все'
                    : zoneSrcFilter === 'mintc'
                      ? 'Зоны: mintc'
                      : zoneSrcFilter === 'sppi'
                        ? 'Зоны: sppi'
                        : 'Зоны: свои'}
                </Button>
                <Button
                  size="sm"
                  icon={Layers}
                  onClick={() => setShowLayers((v) => !v)}
                  title="Показать/скрыть условные обозначения"
                  aria-label="Условные обозначения"
                />
                <Button
                  size="sm"
                  variant={showTwinOverlay ? 'primary' : 'default'}
                  onClick={() => setShowTwinOverlay((v) => !v)}
                  title="Двойник vs реальность (D3)"
                  aria-label="Оверлей двойника"
                >
                  Два
                </Button>
              </>
            }
          >
            {/* Легенда слоёв — ВНУТРИ тела панели, под картой.
                Раньше она была в actions (шапке панели) и на телефоне
                разворачивалась в колонку прямо поверх карты, закрывая её центр. */}
            {showLayers && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line px-2 py-1.5 text-[10px] text-ink-muted">
                {MAP_LAYER_META.map((l) => (
                  <span key={l.id} className="flex items-center gap-1">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: l.color }}
                      aria-hidden
                    />
                    {l.label}
                  </span>
                ))}
                {showTwinOverlay && twinTrust && (
                  <span className="flex items-center gap-1 text-pink-400">
                    Двойник: hit-in-SEP {twinTrust.pos_hit_in_sep ?? '—'} ·
                    TPR {twinTrust.conf_tpr ?? '—'} · FPR {twinTrust.conf_fpr ?? '—'}
                  </span>
                )}
              </div>
            )}
            <MapView
              bbox={bbox}
              is3d={is3d}
              points={gridPoints}
              lines={gridLines}
              polygons={gridPolygons}
              geoPoints={splitTraffic(traffic.data ?? []).cooperative}
              trailFeatures={trajFeats.trails}
              predict10Features={trajFeats.p10}
              predict60Features={trajFeats.p60}
              overlayRealFeatures={showTwinOverlay ? twinOverlayFeats.reality : undefined}
              overlayTwinFeatures={showTwinOverlay ? twinOverlayFeats.twin : undefined}
              onConflictArcClick={setConflictCardDrone}
              conflictCard={
                conflictPair ? (
                  <ConflictPairCard
                    pair={conflictPair.pair}
                    horizons={conflictPair.horizons}
                    missM={conflictPair.missM}
                    tcaS={conflictPair.tcaS}
                    onClose={() => setConflictCardDrone(null)}
                  />
                ) : undefined
              }
              onPick={(lat, lon) => {
                // Клик по карте смещает интересующую область
                setBbox({
                  lat1: Math.max(-89, lat - 0.15),
                  lon1: Math.max(-179, lon - 0.2),
                  lat2: Math.min(89, lat + 0.15),
                  lon2: Math.min(179, lon + 0.2),
                })
              }}
            />
          </Panel>

          {/* Строка состояния под картой */}
          <div className="ut-panel shrink-0 px-2.5 py-1.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <TrackSummary tracks={tracks} />
              <span className="ut-metric text-[10px] text-ink-muted">
                Цели: {fmtAge(traffic.updatedAt)}
              </span>
              {traffic.error && (
                <span className="ut-state-warning text-[10px]">
                  {fmtDegradedReason(traffic.degradedReason)}
                </span>
              )}
              {conflict && (
                <span className="ut-state-critical-error ut-pulsing rounded px-1.5 py-0.5 text-[10px]">
                  Конфликт: {conflict.track.callsign} на {fmtDistance(conflict.distance)},
                  Δh {conflict.altDiff} м
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ---------- СИТУАЦИОННЫЙ ЦЕНТР (тёмный) ---------- */}
        <div
          className={`${isMobile ? 'min-h-0 flex-1' : ''} flex min-h-0 flex-col gap-2`}
          style={
            isMobile
              ? { display: mobileScreen === 'situational' || mobileScreen === 'auditor' ? 'flex' : 'none' }
              : undefined
          }
        >
          <Panel
            title="Ситуационный центр"
            icon={Monitor}
            state={<StateBadge state={theme === 'situational' ? 'active' : 'pending'}>ТЁМНАЯ</StateBadge>}
            className="min-h-0 flex-1"
            bodyClassName="flex flex-col"
          >
            <div className="shrink-0 border-b border-line px-2 py-1.5">
              <Tabs<SitTab>
                active={sitTab}
                onChange={setSitTab}
                tabs={[
                  ...(sitTabs.includes('tracks')
                    ? [{ id: 'tracks' as const, label: 'Цели', icon: Radar, badge: tracks.length }]
                    : []),
                  ...(sitTabs.includes('range')
                    ? [{ id: 'range' as const, label: 'Дальность', icon: Crosshair }]
                    : []),
                  ...(sitTabs.includes('trust')
                    ? [{ id: 'trust' as const, label: 'Доверие', icon: ShieldCheck }]
                    : []),
                  ...(sitTabs.includes('l3') ||
                  auth.me?.roles?.some((r) => r.startsWith('l3_'))
                    ? [{ id: 'l3' as const, label: 'Тренажёр L3', icon: GraduationCap }]
                    : []),
                  ...(sitTabs.includes('weather')
                    ? [
                        {
                          id: 'weather' as const,
                          label: 'Метео',
                          icon: CloudSun,
                          badge: weather.data?.icao ? 1 : 0,
                        },
                      ]
                    : []),
                  ...(sitTabs.includes('geofences')
                    ? [
                        {
                          id: 'geofences' as const,
                          label: 'Геозоны',
                          icon: Layers,
                          badge: geofences.data?.length,
                        },
                      ]
                    : []),
                  ...(sitTabs.includes('nebosvod')
                    ? [{ id: 'nebosvod' as const, label: 'Небосвод', icon: CloudCog }]
                    : []),
                  ...(sitTabs.includes('manage')
                    ? [{ id: 'manage' as const, label: 'Управление', icon: Users }]
                    : []),
                  ...(sitTabs.includes('auditor')
                    ? [{ id: 'auditor' as const, label: 'Аудит', icon: ShieldCheck }]
                    : []),
                ]}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {sitTab === 'auditor' && (
                <div className="flex flex-col gap-2">
                  <AuditorScreen trust={trust} declarations={declarations} />
                  <IncidentsJournal incidents={incidents} expanded />
                </div>
              )}
              {sitTab === 'tracks' && (
                <TrackTable
                  tracks={tracks}
                  selectedId={selectedTrackId}
                  onSelect={setSelectedTrackId}
                  lockedId={ownshipId}
                />
              )}
              {sitTab === 'range' && (
                <RangeBearing
                  ownship={ownship}
                  target={selected}
                  tracks={tracks}
                  onLock={(t) => {
                    setOwnshipId(t.id)
                    setSelectedTrackId(null)
                  }}
                  onUnlock={() => setOwnshipId(null)}
                  onSelectTarget={setSelectedTrackId}
                />
              )}
              {sitTab === 'trust' && <TrustPanel trust={trust} onRefresh={trust.refresh} />}
              {sitTab === 'l3' && (
                <div className="flex flex-col gap-2">
                  <ReplayPlayer
                    isInstructor={isInstructor}
                    onFrame={setReplayData}
                  />
                  <L3Panel src={trafficSrc} onSrc={setTrafficSrc} />
                </div>
              )}
              {sitTab === 'weather' && (
                <WeatherPanel
                  weather={weather.data}
                  onRefresh={weather.refresh}
                  busy={weather.status === 'loading'}
                />
              )}
              {sitTab === 'geofences' && (
                <GeofencePanel
                  geofences={geofences.data ?? []}
                  onCreate={handleCreateGeofence}
                  onDelete={handleDeleteGeofence}
                  busy={geoBusy}
                />
              )}
              {sitTab === 'nebosvod' && (
                <NebosvodPanel
                  status={nebosvod}
                  geofences={geofences.data ?? []}
                  onSync={handleNebosvodSync}
                  onToggle={handleNebosvodToggle}
                  syncing={nebosvodSyncing}
                />
              )}
              {sitTab === 'manage' && (
                <UsersRolesPanel
                  users={users}
                  onChangeRole={(id: string, role: Role) =>
                    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)))
                  }
                  onBindAsset={(id: string, serial: string) =>
                    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, assetSerial: serial } : u)))
                  }
                />
              )}
            </div>
          </Panel>
        </div>
      </main>
    </div>
  )
}

/* ============================================================================
 * СПИСОК ДЕКЛАРАЦИЙ (заявки СППИ) — с согласованием
 * ========================================================================= */
/** П.4 (2026-10-06): претест центра района заявки против запретных зон прода.
 * GET /api/pilot/precheck — бэкенд PostGIS ST_Contains. null = проверка не
 * выполнялась (нет area/бэкенд недоступен) — бейдж не показываем. */
function useZonePrecheck(decls: FlightDeclaration[]): Map<string, { ok: boolean; message: string; zones: string[] } | null> {
  const [map, setMap] = useState<Map<string, { ok: boolean; message: string; zones: string[] } | null>>(new Map())
  const key = useMemo(
    () => decls.map((d) => `${d.id}:${d.area?.length ?? 0}`).join('|'),
    [decls],
  )
  useEffect(() => {
    let dead = false
    const ctrl = new AbortController()
    async function run(): Promise<void> {
      const next = new Map<string, { ok: boolean; message: string; zones: string[] } | null>()
      for (const d of decls) {
        const area = Array.isArray(d.area) ? (d.area as Array<[number, number]>) : null
        if (!area || area.length < 3 || d.id == null) {
          next.set(String(d.id), null)
          continue
        }
        // центр bbox района
        const lats = area.map((p) => p[1])
        const lons = area.map((p) => p[0])
        const lat = (Math.min(...lats) + Math.max(...lats)) / 2
        const lon = (Math.min(...lons) + Math.max(...lons)) / 2
        try {
          const res = await fetch(
            `/api/pilot/precheck?lat=${lat}&lon=${lon}`,
            { signal: ctrl.signal },
          )
          if (!res.ok) throw new Error(String(res.status))
          const j = (await res.json()) as { ok?: boolean; hits?: Array<{ zone?: string }>; message?: string }
          next.set(String(d.id), {
            ok: Boolean(j.ok),
            message: j.message ?? '',
            zones: (j.hits ?? []).map((h) => String(h.zone ?? '')),
          })
        } catch {
          next.set(String(d.id), null)
        }
      }
      if (!dead) setMap(next)
    }
    void run()
    return () => {
      dead = true
      ctrl.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return map
}

function DeclarationsList({
  declarations,
  onReview,
  weather,
  readOnly = false,
  reviewConflict = null,
}: {
  declarations: AsyncState<FlightDeclaration[]>
  onReview: (id: string, approve: boolean, force?: boolean) => void
  weather: WeatherSnapshot | null
  /** Роль без права согласования (аудитор): список виден, кнопок нет.
      Без этого аудитору показывались кнопки «Согласовать/Отклонить», ведущие
      на PUT /api/declarations/{id}/review — то есть в 403. */
  readOnly?: boolean
  /** §6.2: активный конфликт согласования (id+зоны) — блок с «Одобрить всё равно». */
  reviewConflict?: { id: string; zones: string[]; message: string } | null
}) {
  const list = declarations.data ?? []
  const prechecks = useZonePrecheck(list)

  if (declarations.status !== 'ok' && list.length === 0) {
    return (
      <EmptyHint>
        <span className="flex flex-col items-center gap-1.5">
          <ClipboardList size={20} className="text-ink-muted" aria-hidden />
          Декларации недоступны
          <span className="max-w-[240px]">
            {fmtDegradedReason(declarations.degradedReason)} — СППИ требует работающего
            Flight Blender и Keycloak.
          </span>
        </span>
      </EmptyHint>
    )
  }

  return (
    <div className="space-y-1.5 p-2.5">
      <StatusCounters declarations={list} />
      <WeatherStrip weather={weather} />
      {list.map((d, i) => {
        // Канонический статус вместо is_approved-бинарника (задача №7).
        const parsed = normalizeDeclarationStatus(d)
        const status: DeclarationStatus = parsed.status
        const approved = status === 'ready'
        const tone = DECLARATION_STATUS_TONE[status]
        return (
          <div
            key={d.id ?? i}
            data-status={status}
            className={`rounded-md border px-2 py-1.5 ut-stated ut-state-${tone}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[11px] font-semibold">{d.id ?? `Заявка ${i + 1}`}</span>
              <StateBadge state={tone}>
                {DECLARATION_STATUS_LABEL[status]}
              </StateBadge>
            </div>
            {/* П.4 + §6.2 (2026-10-07): предупреждение о запретных зонах.
                Приоритет — СЕРВЕРНЫЙ претест (d.precheck.checked): он честный
                (PostGIS, претест района), а не клиентский по центру bbox.
                Клиентский useZonePrecheck оставлен как fallback для случаев,
                когда серверный претест не проверен (checked=false). */}
            {(() => {
              const sp = d.precheck
              if (sp?.checked) {
                return sp.ok ? (
                  <div className="mt-1 rounded-md border px-2 py-1 text-[10px] ut-state-approved text-success">
                    Зон в районе заявки нет — серверный претест пройден
                  </div>
                ) : (
                  <div className="mt-1 rounded-md border px-2 py-1 text-[10px] ut-state-critical-error text-critical">
                    ⚠ Район заявки пересекает запретные зоны: {sp.zones.join(', ')} — согласование обязательно
                  </div>
                )
              }
              const pc = d.id != null ? prechecks.get(String(d.id)) : undefined
              if (pc == null) return null
              return pc.ok ? (
                <div className="mt-1 rounded-md border px-2 py-1 text-[10px] ut-state-approved text-success">
                  Зон в районе заявки нет — претест пройден
                </div>
              ) : (
                <div className="mt-1 rounded-md border px-2 py-1 text-[10px] ut-state-critical-error text-critical">
                  ⚠ Входишь в запретную зону: {pc.zones.join(', ')} — согласование обязательно
                </div>
              )
            })()}
            {/* §6.2: конфликт при согласовании — зоны + кнопка «Одобрить всё равно» */}
            {reviewConflict?.id === String(d.id) && (
              <div className="mt-1.5 rounded-md border border-critical px-2 py-1.5 text-[10px] ut-state-critical-error">
                <div className="font-semibold text-critical">Конфликт с запретной зоной</div>
                <div className="mt-0.5 text-ink-muted">{reviewConflict.message}</div>
                {reviewConflict.zones.length > 0 && (
                  <div className="mt-0.5">Зоны: {reviewConflict.zones.join(', ')}</div>
                )}
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => onReview(d.id as string, true, true)}
                  >
                    Одобрить всё равно
                  </Button>
                  <Button size="sm" variant="default" onClick={() => onReview(d.id as string, false)}>
                    Отклонить
                  </Button>
                </div>
              </div>
            )}
            <div className="ut-metric mt-0.5 flex flex-wrap gap-x-3 text-[10px] text-ink-muted">
              <span>Класс: {d.uas_class ?? '—'}</span>
              {d.provider_id ? (
                <span title={`Источник: ${d.provider_kind ?? 'не указан'}`}>
                  ИД ист.: {String(d.provider_id).slice(0, 10)}
                </span>
              ) : null}
              <span>Высота: {d.alt_m !== undefined ? `${d.alt_m} м` : '—'}</span>
              <span>{d.operator ? String(d.operator) : ''}</span>
              {parsed.raw !== undefined && (
                <span className="text-warning" title="Значение от бэкенда не совпало с каноническим">
                  бэкенд: {parsed.raw || '—'}
                </span>
              )}
            </div>
            {!approved && !readOnly && (
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                <Button size="sm" variant="primary" onClick={() => d.id && onReview(d.id, true)}>
                  Согласовать
                </Button>
                <Button size="sm" variant="danger" onClick={() => d.id && onReview(d.id, false)}>
                  Отклонить
                </Button>
              </div>
            )}
            {approved && d.approved_by && (
              <div className="mt-0.5 text-[10px] text-ink-muted">Согласовал: {d.approved_by}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Переиспользуемый разбор координат в других модулях */
export { parseCoords, parseArea, DEFAULT_SPP }

/* ============================================================================
 * КАРТОЧКА КОНФЛИКТНОЙ ПАРЫ (2026-10-07g)
 * Клик по красной дуге прогноза → карточка: кто с кем, miss_m, tca_s,
 * горизонты, на которых пара ближе ZG_PRED_SEP_M. Мигание при tca<10с.
 * ========================================================================= */
function ConflictPairCard({
  pair,
  horizons,
  missM,
  tcaS,
  onClose,
}: {
  pair: TrajConflict
  horizons: string[]
  missM: number
  tcaS: number
  onClose: () => void
}) {
  const imminent = tcaS < 10
  return (
    <div
      className={`ut-panel border px-2.5 py-2 text-[11px] shadow-lg ${imminent ? 'ut-pulsing' : ''}`}
      style={{
        borderColor: imminent ? 'var(--ut-color-critical)' : undefined,
        background: 'var(--ut-color-surface, #0A1120)',
      }}
      data-conflict-card={pair.a}
      role="dialog"
      aria-label="Карточка конфликта"
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-bold" style={{ color: '#ef4444' }}>
          Прогноз конфликта{imminent ? ' ⚠' : ''}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-ink-muted hover:text-ink"
          aria-label="Закрыть карточку"
        >
          ✕
        </button>
      </div>
      <div className="flex flex-col gap-0.5">
        <span>
          Борта: <b>{pair.a_callsign || pair.a}</b> × <b>{pair.b_callsign || pair.b}</b>
        </span>
        <span>
          Мин. дистанция: <b>{missM.toFixed(0)} м</b> (порог {500} м)
        </span>
        <span>
          Схождение через: <b>{tcaS.toFixed(0)} с</b>
        </span>
        {pair.vsep_ft !== undefined && pair.vsep_ft !== null && (
          <span>
            Вертик. эшелон: <b>{Math.round(pair.vsep_ft)} фт</b> (порог 150 фт)
          </span>
        )}
        <span className="text-ink-muted">
          Горизонт{horizons.length > 1 ? 'ы' : ''}: {horizons.map((h) => `${h} с`).join(', ')}
        </span>
      </div>
    </div>
  )
}
